import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { ArrowLeft, Mail, Check, Printer, Bell, Download, AlertTriangle } from 'lucide-react';
import Kommentarer from '../components/Kommentarer';

const StatusBadge = ({ s }) => {
  const map = { betalt: 'badge-success', sendt: 'badge-purple', usendt: 'badge-neutral',
                forfalt: 'badge-danger', kreditnota: 'badge-warning' };
  return <span className={`badge ${map[s] || 'badge-neutral'}`}>{s}</span>;
};

const FakturaVis = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [firma, setFirma] = useState(null);
  const [epostOpen, setEpostOpen] = useState(false);
  const [til, setTil] = useState('');
  const [emne, setEmne] = useState('');
  const [melding, setMelding] = useState('');
  const [busy, setBusy] = useState(false);
  const [feil, setFeil] = useState('');

  const last = () => Promise.all([
    api.get(`/faktura/${id}`).then((r) => setF(r.data)),
    api.get('/firma').then((r) => setFirma(r.data)),
  ]);
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [id]);

  const settStatus = async (status) => {
    await api.post(`/faktura/${id}/status`, { status });
    last();
  };

  const purr = async () => {
    if (!window.confirm(`Send purring? Dette øker purrenivå og legger på gebyr.`)) return;
    try { await api.post(`/faktura/${id}/purring`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  const ehf = () => {
    const tok = localStorage.getItem('token');
    fetch(`${api.defaults.baseURL}/faktura/${id}/ehf`, {
      headers: { Authorization: `Bearer ${tok}` },
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `EHF-${f.fakturanummer}.xml`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    });
  };

  const aapneEpost = () => {
    setTil(f.kunde_epost || '');
    setEmne(`Faktura ${f.fakturanummer} fra ${firma.navn}`);
    setMelding('');
    setFeil('');
    setEpostOpen(true);
  };
  const sendEpost = async () => {
    setBusy(true); setFeil('');
    try {
      await api.post(`/faktura/${id}/send-epost`, { til, emne, melding });
      setEpostOpen(false); last();
    } catch (e) { setFeil(e.response?.data?.error || 'Feil ved sending.'); }
    finally { setBusy(false); }
  };

  if (!f || !firma) return <span className="fm-text-muted">Laster…</span>;

  const smtpKonfig = !!firma.smtp_host && !!firma.smtp_avsender;

  return (
    <>
      {firma.logo && (
        <div style={{ marginBottom: 12 }}>
          <img src={firma.logo} alt="logo" style={{ maxHeight: 70, maxWidth: 240, objectFit: 'contain' }} />
        </div>
      )}
      <PageHeader
        title={`Faktura ${f.fakturanummer}`}
        subtitle={f.kunde_navn}
        actions={<>
          <button className="btn-ghost" onClick={() => nav('/faktura')}><ArrowLeft size={14} /> Tilbake</button>
          <button className="btn-purple" onClick={aapneEpost} title={smtpKonfig ? 'Send via e-post' : 'Sett opp SMTP under Innstillinger først'}>
            <Mail size={14} /> Send e-post
          </button>
          <button className="btn-success" onClick={() => settStatus('betalt')}><Check size={14} /> Merk betalt</button>
          <button className="btn-ghost" onClick={purr} disabled={f.status === 'betalt' || f.purrenivaa >= 3}
                  style={{ color: 'var(--theme-warning)' }}><Bell size={14} /> Purr</button>
          <button className="btn-ghost" onClick={ehf}><Download size={14} /> EHF/XML</button>
          <button className="btn-ghost" onClick={() => window.print()}><Printer size={14} /> Skriv ut</button>
        </>}
      />

      {f.purrenivaa > 0 && (
        <Card style={{ marginBottom: 12, borderLeft: '3px solid var(--theme-warning)' }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <AlertTriangle size={14} style={{ color: 'var(--theme-warning)' }} />
            <strong style={{ fontSize: 12 }}>
              Purring nivå {f.purrenivaa} {f.purrenivaa === 3 && '(inkassovarsel)'}
            </strong>
            <span className="fm-text-muted" style={{ fontSize: 11 }}>
              Sendt {dato(f.purret_dato)} · Gebyr: {kr(f.purregebyr)} · Rente: {kr(f.forsinkelsesrente)}
            </span>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 12 }}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Fra</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{firma.navn}</div>
            {firma.adresse && <div style={{ fontSize: 12 }}>{firma.adresse}</div>}
            <div style={{ fontSize: 12 }}>{firma.postnr} {firma.sted}</div>
            {firma.orgnr && <div style={{ fontSize: 12 }}>Org.nr: {firma.orgnr}</div>}
            {firma.kontonr && <div style={{ fontSize: 12 }}>Kontonr: {firma.kontonr}</div>}
          </div>
          <div>
            <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Til</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{f.kunde_navn}</div>
            {f.kunde_adresse && <div style={{ fontSize: 12 }}>{f.kunde_adresse}</div>}
            <div style={{ fontSize: 12 }}>{f.kunde_postnr} {f.kunde_sted}</div>
            {f.kunde_orgnr && <div style={{ fontSize: 12 }}>Org.nr: {f.kunde_orgnr}</div>}
            {f.kunde_epost && <div style={{ fontSize: 12 }}>{f.kunde_epost}</div>}
          </div>
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginTop: 16,
                                       paddingTop: 16, borderTop: '1px solid var(--theme-border)' }}>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Fakturadato</div><div style={{ fontSize: 13 }}>{dato(f.dato)}</div></div>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Forfall</div><div style={{ fontSize: 13 }}>{dato(f.forfall)}</div></div>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>KID</div>
               <div style={{ fontSize: 13, fontFamily: 'monospace' }}>{f.kid || '–'}</div></div>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Status</div><div style={{ marginTop: 2 }}><StatusBadge s={f.status} /></div></div>
          <div style={{ textAlign: 'right' }}><div className="fm-text-muted" style={{ fontSize: 11 }}>Total</div>
               <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-accent)' }}>{kr(f.sum_ink_mva + (f.purregebyr || 0) + (f.forsinkelsesrente || 0))}</div></div>
        </div>
        {f.sendt_epost_til && (
          <div className="fm-text-muted" style={{ fontSize: 11, marginTop: 8 }}>
            Sendt på e-post til {f.sendt_epost_til} ({dato(f.sendt_epost_dato)})
          </div>
        )}
      </Card>

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Beskrivelse</th>
              <th style={{ textAlign: 'right' }}>Antall</th>
              <th style={{ textAlign: 'right' }}>Pris</th>
              <th style={{ textAlign: 'right' }}>MVA %</th>
              <th style={{ textAlign: 'right' }}>Eks. MVA</th>
              <th style={{ textAlign: 'right' }}>Ink. MVA</th>
            </tr>
          </thead>
          <tbody>
            {f.linjer.map((l) => (
              <tr key={l.id}>
                <td>{l.beskrivelse}</td>
                <td style={{ textAlign: 'right' }}>{l.antall}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.enhetspris)}</td>
                <td style={{ textAlign: 'right' }}>{l.mva_sats}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.belop_eks_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.belop_ink_mva)}</td>
              </tr>
            ))}
            {f.purregebyr > 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'right' }}>Purregebyr</td>
                  <td style={{ textAlign: 'right' }}>{kr(f.purregebyr)}</td></tr>
            )}
            {f.forsinkelsesrente > 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'right' }}>Forsinkelsesrente</td>
                  <td style={{ textAlign: 'right' }}>{kr(f.forsinkelsesrente)}</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Sum eks. MVA</td>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>{kr(f.sum_eks_mva)}</td>
            </tr>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>MVA</td>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>{kr(f.sum_mva)}</td>
            </tr>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>Total å betale</td>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: 'var(--theme-accent)' }}>
                {kr(f.sum_ink_mva + (f.purregebyr || 0) + (f.forsinkelsesrente || 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>

      {f.notater && (
        <Card style={{ marginTop: 12 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600 }}>Notater</h3>
          <p style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{f.notater}</p>
        </Card>
      )}

      <Kommentarer entitet="faktura" entitetId={f.id} />

      <Modal open={epostOpen} onClose={() => setEpostOpen(false)} title="Send faktura på e-post" width={560}>
        {!smtpKonfig && (
          <div className="badge badge-warning" style={{ padding: 6, marginBottom: 8, display: 'block' }}>
            SMTP er ikke konfigurert. Gå til Innstillinger → E-post for å sette opp.
          </div>
        )}
        {feil && <div className="badge badge-danger" style={{ padding: 6, marginBottom: 8 }}>{feil}</div>}
        <label>Mottaker</label>
        <input value={til} onChange={(e) => setTil(e.target.value)} placeholder="kunde@firma.no" style={{ marginBottom: 8 }} />
        <label>Emne</label>
        <input value={emne} onChange={(e) => setEmne(e.target.value)} style={{ marginBottom: 8 }} />
        <label>Melding (valgfritt – auto-fylles hvis tom)</label>
        <textarea value={melding} onChange={(e) => setMelding(e.target.value)} rows={6} />
        <div className="flex" style={{ gap: 6, marginTop: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setEpostOpen(false)}>Avbryt</button>
          <button className="btn-primary" onClick={sendEpost} disabled={busy || !til || !smtpKonfig}>
            <Mail size={12} /> {busy ? 'Sender…' : 'Send'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default FakturaVis;
